import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  adminListEstimates,
  adminDeleteEstimate,
} from "@/lib/adminInbox.functions";
import { Button } from "@/components/ui/button";
import { Trash2, Search, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/tools/estimates")({
  head: () => ({
    meta: [
      { title: "Saved Estimates | Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EstimatesBrowser,
});

type Row = Awaited<ReturnType<typeof adminListEstimates>>[number];

const currency = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n ?? 0);

function EstimatesBrowser() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  async function load() {
    try {
      setRows(await adminListEstimates());
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.name, r.client_name, r.client_email, r.client_phone, r.client_address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [rows, q]);

  const totalValue = useMemo(
    () => filtered.reduce((sum, r) => sum + Number(r.total ?? 0), 0),
    [filtered],
  );

  async function remove(id: string, name: string) {
    if (!confirm(`Delete estimate "${name}"? This cannot be undone.`)) return;
    await adminDeleteEstimate({ data: { id } });
    load();
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Saved estimates</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered.length} estimate{filtered.length === 1 ? "" : "s"} •{" "}
            {currency(totalValue)} total
          </p>
        </div>
        <Link to="/estimator">
          <Button>New estimate</Button>
        </Link>
      </div>

      <div className="mb-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, client, email, phone, address…"
          className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-foreground/40"
        />
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      {!rows ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No estimates match.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Client</th>
                <th className="p-3">Contact</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Updated</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-muted-foreground">
                    {r.client_name || <span className="text-muted-foreground/60">—</span>}
                    {r.client_address && (
                      <div className="mt-0.5 text-xs text-muted-foreground/70">
                        {r.client_address}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {r.client_email && <div>{r.client_email}</div>}
                    {r.client_phone && <div>{r.client_phone}</div>}
                    {!r.client_email && !r.client_phone && (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-medium">
                    {currency(Number(r.total ?? 0))}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {new Date(r.updated_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to="/estimator"
                      search={{ load: r.id } as any}
                      className="mr-3 inline-flex items-center gap-1 text-brand-text underline underline-offset-2"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Open
                    </Link>
                    <button
                      onClick={() => remove(r.id, r.name)}
                      className="inline-flex items-center gap-1 text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
